<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">{{ isEditMode ? 'Edit Distribution' : 'Create Distribution' }}</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading destinations...</p>
      </div>

      <form v-else @submit.prevent="onSubmit">
        <!-- Distribution Name -->
        <div class="mb-3">
          <label class="form-label">Distribution Name <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="formData.name"
            placeholder="Enter distribution name"
            required
          />
        </div>

        <!-- Destination Platform -->
        <div class="mb-3">
          <label class="form-label">Destination Platform <span class="text-danger">*</span></label>
          <select class="form-select" v-model="formData.destinationId" required>
            <option value="">Select destination...</option>
            <option v-for="dest in destinations" :key="dest.id" :value="dest.id">
              {{ dest.destination_name }}
            </option>
          </select>
        </div>

        <!-- Entity Types -->
        <div class="mb-3">
          <label class="form-label">Entity Types</label>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="maids" v-model="formData.entityTypes.maids" />
            <label class="form-check-label" for="maids">MAIDs</label>
          </div>
        </div>

        <!-- Schedule -->
        <div class="mb-3">
          <label class="form-label">Schedule <span class="text-danger">*</span></label>
          <select class="form-select" v-model="formData.scheduleType">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <small class="text-muted">
            Cron Expression: <code>{{ fullSchedule }}</code>
          </small>
        </div>

        <!-- Date Range -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Start Date <span class="text-danger">*</span></label>
            <input type="date" class="form-control" v-model="formData.startDate" required />
          </div>
          <div class="col-md-6">
            <label class="form-label">End Date <span class="text-danger">*</span></label>
            <input type="date" class="form-control" v-model="formData.endDate" required />
          </div>
        </div>

        <!-- Advanced Options -->
        <div class="mb-3">
          <a href="#" class="text-decoration-none" @click.prevent="showAdvanced = !showAdvanced">
            <i :class="showAdvanced ? 'fa fa-chevron-down' : 'fa fa-chevron-right'" class="me-2"></i>
            Advanced Options
          </a>
          <div v-if="showAdvanced" class="mt-3 p-3 bg-light rounded">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Lookback Window (days)</label>
                <input type="number" class="form-control" v-model.number="formData.lookbackWindowDays" min="1" />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Backfill Period (days)</label>
                <input type="number" class="form-control" v-model.number="formData.backfillPeriodDays" min="1" />
              </div>
            </div>
          </div>
        </div>
      </form>

      <div v-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>
    </div>
    <div class="card-footer">
      <button
        type="button"
        class="btn btn-primary"
        @click="onSubmit"
        :disabled="!isFormValid || saving"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-2" />
        {{ isEditMode ? 'Update' : 'Create' }} Distribution
      </button>
      <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'AddDistribution',
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      showAdvanced: false,
      destinations: [],
      formData: {
        name: '',
        destinationId: '',
        entityTypes: { maids: true },
        scheduleType: 'daily',
        startDate: '',
        endDate: '',
        lookbackWindowDays: 1,
        backfillPeriodDays: 1,
      },
    }
  },
  computed: {
    isEditMode() {
      return this.modalData?.distribution != null
    },
    audienceId() {
      return this.modalData?.audienceId
    },
    isFormValid() {
      return (
        this.formData.name.trim() !== '' &&
        this.formData.destinationId !== '' &&
        this.formData.startDate !== '' &&
        this.formData.endDate !== ''
      )
    },
    fullSchedule() {
      const scheduleMap = {
        daily: '* * * * *',
        weekly: '* * * * 0',
        monthly: '* * 1 * *',
      }
      return scheduleMap[this.formData.scheduleType] || '* * * * *'
    },
  },
  methods: {
    ...mapActions({
      fetchDistributionDestinations: 'audience/fetchDistributionDestinations',
      createDistribution: 'audience/createDistribution',
      updateDistribution: 'audience/updateDistribution',
      hideModal: 'hideModal',
      addToast: 'addToast',
    }),
    async loadDestinations() {
      this.loading = true
      try {
        this.destinations = await this.fetchDistributionDestinations()
      } catch (error) {
        this.error = 'Failed to load destinations'
      } finally {
        this.loading = false
      }
    },
    populateFormFromDistribution() {
      if (this.modalData?.distribution) {
        const dist = this.modalData.distribution
        this.formData.name = dist.name || ''
        this.formData.destinationId = dist.destination_id || dist.Destination_id || ''
        this.formData.startDate = dist.start_date || ''
        this.formData.endDate = dist.end_date || ''
        this.formData.lookbackWindowDays = dist.lookback_window_days || 1
        this.formData.backfillPeriodDays = dist.backfill_period_days || 1

        const entityTypes = dist.entity_types || ''
        this.formData.entityTypes.maids = entityTypes.toLowerCase().includes('maid')

        // Parse schedule
        const schedule = dist.schedule || ''
        if (schedule.includes('0') && schedule.split(' ')[4] === '0') {
          this.formData.scheduleType = 'weekly'
        } else if (schedule.includes('1') && schedule.split(' ')[2] === '1') {
          this.formData.scheduleType = 'monthly'
        } else {
          this.formData.scheduleType = 'daily'
        }
      }
    },
    async onSubmit() {
      if (!this.isFormValid) return

      this.saving = true
      this.error = null

      try {
        const entityTypes = this.formData.entityTypes.maids ? 'MAIDs' : ''

        const distributionData = {
          audience_id: parseInt(this.audienceId),
          Destination_id: this.formData.destinationId,
          name: this.formData.name,
          entity_types: entityTypes,
          lookback_window_days: this.formData.lookbackWindowDays,
          backfill_period_days: this.formData.backfillPeriodDays,
          schedule: this.fullSchedule,
          start_date: this.formData.startDate,
          end_date: this.formData.endDate,
        }

        if (this.isEditMode) {
          await this.updateDistribution({
            audienceId: this.audienceId,
            distributionId: this.modalData.distribution.id,
            distributionData,
          })
          this.addToast({ type: 'success', message: 'Distribution updated successfully' })
        } else {
          await this.createDistribution({
            audienceId: this.audienceId,
            distributionData,
          })
          this.addToast({ type: 'success', message: 'Distribution created successfully' })
        }

        this.hideModal()
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to save distribution'
      } finally {
        this.saving = false
      }
    },
  },
  mounted() {
    this.loadDestinations()
    if (this.isEditMode) {
      this.populateFormFromDistribution()
    }
  },
}
</script>
